import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeClock } from "@mui/x-date-pickers/TimeClock";

import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Badge,
  Button,
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import ImageIcon from "@mui/icons-material/Image";

// Import API function
import { fetchMonthlyPosts } from "../../Action/api";
import { getAuthTokenPayload } from "../../Utils/index";

// Enable the plugins
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(timezone);
dayjs.extend(utc);

const PLATFORM_COLORS = {
  Instagram: "#E1306C",
  Facebook: "#1877F2",
  Twitter: "#1DA1F2",
  X: "#111827",
  LinkedIn: "#0A66C2",
  YouTube: "#FF0000",
  TikTok: "#000000",
  Pinterest: "#BD081C",
  Default: "#6B7280",
};

export const CalendarView = ({
  open = true,
  onClose = () => {},
  onDateTimeSelect = () => {},
  selectedDateTime = null,
  loading: externalLoading = false,
  title = "Schedule",
}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(
    selectedDateTime ? dayjs(selectedDateTime) : null
  );
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedTime, setSelectedTime] = useState(
    selectedDateTime ? dayjs(selectedDateTime).format("HH:mm") : "09:00"
  );

  // First, add a ref to track if the API call is in progress
  const isLoadingRef = useRef(false);

  // Define postsData memo before any hooks that depend on it
  const postsData = useMemo(() => {
    return Array.isArray(posts) ? posts : [];
  }, [posts]);

  // Effect to load posts when component mounts and when month changes
  useEffect(() => {
    const loadPosts = async () => {
      // Prevent multiple simultaneous calls
      if (isLoadingRef.current) return;

      try {
        isLoadingRef.current = true;
        setLoading(true);

        const clientId = getAuthTokenPayload()?.clientId;
        const monthYear = currentMonth.format("MMM-YYYY"); // Format: Oct-2025

        console.log("Fetching posts for:", monthYear); // Debug log

        const response = await fetchMonthlyPosts(monthYear, clientId);

        if (response.success) {
          setPosts(response.data || []);
        } else {
          setError(response.message);
          setPosts([]);
        }
      } catch (err) {
        console.error("Error loading posts:", err);
        setError("Failed to load scheduled posts");
        setPosts([]);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    // Call loadPosts when calendar opens or month changes
    if (open) {
      loadPosts();
      console.log("Calendar opened, fetching posts..."); // Debug log
    }
  }, [open, currentMonth]); // Keep only these dependencies

  // Reset when parent changes selection
  useEffect(() => {
    if (selectedDateTime) {
      const d = dayjs(selectedDateTime);
      setSelectedDate(d);
      setSelectedTime(d.format("HH:mm")); // force minutes to 00
      setCurrentMonth(d);
    }
  }, [selectedDateTime]);

  // Auto-jump to the nearest month that contains posts when opening or when there is no selection - ADDED
  useEffect(() => {
    // Only run this effect if we're not already loading
    if (!loading && !selectedDate && postsData && postsData.length > 0) {
      const dates = postsData
        .map((p) => dayjs(p.scheduleDateTime))
        .filter((d) => d.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());

      if (dates.length === 0) return;

      const today = dayjs();
      const upcoming = dates.find(
        (d) => d.isSame(today, "month") || d.isAfter(today, "day")
      );
      const target = upcoming || dates[dates.length - 1];

      // Only update if the target month is different and we're not already loading
      if (
        target &&
        !target.isSame(currentMonth, "month") &&
        !isLoadingRef.current
      ) {
        setCurrentMonth(target);
      }
    }
  }, [postsData, selectedDate, loading]); // Remove currentMonth from dependencies

  // Helpers
  const getPlatformColor = (platform) =>
    PLATFORM_COLORS[platform] || PLATFORM_COLORS.Default;

  const availablePlatforms = useMemo(() => {
    const all = new Set();
    postsData.forEach((p) => (p.platforms || []).forEach((pl) => all.add(pl)));
    return ["all", ...Array.from(all)];
  }, [postsData]);

  // Build full calendar grid for the visible month
  const calendarDays = useMemo(() => {
    const start = currentMonth.startOf("month").startOf("week");
    const end = currentMonth.endOf("month").endOf("week");
    const days = [];
    let day = start;
    while (day.isBefore(end) || day.isSame(end, "day")) {
      days.push(day);
      day = day.add(1, "day");
    }
    return days;
  }, [currentMonth]);

  // Get local timezone from browser
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Posts for a specific day (handles scheduledDateTime or scheduledDate) - UPDATED
  const getPostsForDate = (date) => {
    const targetStr = dayjs(date).format("YYYY-MM-DD");
    return postsData.filter((post) => {
      // Convert UTC to local timezone for comparison
      const postDate = dayjs(post.scheduleDateTime)
        .tz(localTimezone)
        .format("YYYY-MM-DD");
      return targetStr === postDate;
    });
  };

  // Filter by platform for a given day’s posts
  const getFilteredPostsForDate = (date) => {
    const posts = getPostsForDate(date);
    if (selectedPlatform === "all") return posts;
    return posts.filter((p) => (p.platforms || []).includes(selectedPlatform));
  };

  const isPastDate = (date) => dayjs(date).isBefore(dayjs(), "day");

  // Visual style per day cell
  const getCellStyle = (day, count) => {
    const isCurrentMonth = day.month() === currentMonth.month();
    const isToday = day.isSame(dayjs(), "day");
    const isSelected = selectedDate && day.isSame(selectedDate, "day");

    // Base
    let border = "#E5E7EB";
    let background = "linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)";
    let textColor = isCurrentMonth ? "#111827" : "#9CA3AF";
    let shadow = "0 1px 3px rgba(0,0,0,0.05)";
    let hoverShadow = "0 10px 30px rgba(0,0,0,0.12)";

    // Scheduled heat levels
    if (count > 0) {
      if (count >= 3) {
        background = "linear-gradient(135deg, #10B981 0%, #059669 100%)";
        textColor = "#FFFFFF";
        border = "#059669";
      } else if (count === 2) {
        background = "linear-gradient(135deg, #34D399 0%, #10B981 100%)";
        textColor = "#053B2C";
        border = "#10B981";
      } else {
        background = "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)";
        textColor = "#065F46";
        border = "#34D399";
      }
    }

    // Today
    if (isToday) {
      background = "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)";
      border = "#2563EB";
      textColor = "#FFFFFF";
      shadow = "0 6px 18px rgba(37,99,235,0.25)";
    }

    // Selected
    if (isSelected) {
      background = "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)";
      border = "#7C3AED";
      textColor = "#FFFFFF";
      shadow = "0 10px 30px rgba(124,58,237,0.35)";
    }

    // Not current month dim
    const opacity = isCurrentMonth ? 1 : 0.65;

    return {
      border,
      background,
      textColor,
      shadow,
      hoverShadow,
      opacity,
    };
  };

  const handlePrev = useCallback(() => {
    if (!isLoadingRef.current) {
      setCurrentMonth((prev) => prev.subtract(1, "month"));
    }
  }, []);

  const handleNext = useCallback(() => {
    if (!isLoadingRef.current) {
      setCurrentMonth((prev) => prev.add(1, "month"));
    }
  }, []);

  const handleToday = useCallback(() => {
    if (!isLoadingRef.current) {
      setCurrentMonth(dayjs());
    }
  }, []);

  // Replace the handler with inline panel behavior (no modal, no drawer)
  const handleSelectDay = (day) => {
    setSelectedDate(day);
  };

  // Force minutes to 00 for any time input
  const normalizeToHour = (value) => {
    const [h] = String(value || "").split(":");
    const hour = String(
      Math.min(23, Math.max(0, parseInt(h, 10) || 0))
    ).padStart(2, "0");
    return `${hour}:00`;
  };

  // UPDATED: Force time to 30-minute intervals (00 or 30)
  const normalizeToHalfHour = (value) => {
    if (!value) return "09:00";

    const dayjsValue = dayjs(value);
    const hour = dayjsValue.hour();
    const minute = dayjsValue.minute();

    // Round to nearest 30-minute interval
    const normalizedMinute = minute < 15 ? "00" : minute < 45 ? "30" : "00";
    const normalizedHour = minute >= 45 ? (hour + 1) % 24 : hour;

    return `${String(normalizedHour).padStart(2, "0")}:${normalizedMinute}`;
  };

  // UPDATED: Check if time is valid (at least 10 minutes from now)
  const isValidTime = (timeString) => {
    if (!selectedDate) return true;

    const now = dayjs().tz(localTimezone);
    const selectedDay = selectedDate.startOf("day");
    const today = now.startOf("day");

    // If not today, any time is valid
    if (!selectedDay.isSame(today, "day")) {
      return true;
    }

    // For today, time must be at least 10 minutes ahead
    const [hour, minute] = timeString.split(":").map(Number);
    const selectedDateTime = selectedDate.hour(hour).minute(minute).second(0);
    const minValidTime = now.add(10, "minute");

    return selectedDateTime.isAfter(minValidTime);
  };

  // UPDATED: Generate all valid time slots with 30-minute intervals
  const getAvailableTimeSlots = () => {
    const slots = [];

    // Generate all 30-minute slots for 24 hours
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 30]) {
        const timeString = `${String(hour).padStart(2, "0")}:${String(
          minute
        ).padStart(2, "0")}`;

        // Only add if it's a valid time (respects 10-minute buffer for today)
        if (isValidTime(timeString)) {
          slots.push({
            value: timeString,
            label: timeString,
            hour,
            minute,
          });
        }
      }
    }

    return slots;
  };

  // UPDATED: Handle time selection from dropdown
  const handleTimeSelect = (timeString) => {
    if (isValidTime(timeString)) {
      setSelectedTime(timeString);
    } else {
      const now = dayjs().tz(localTimezone);
      const nextValidTime = now.add(10, "minute");
      const nextHour = nextValidTime.hour();
      const nextMinute = nextValidTime.minute() < 30 ? 30 : 0;
      const adjustedHour =
        nextValidTime.minute() >= 30 ? (nextHour + 1) % 24 : nextHour;

      setSelectedTime(
        `${String(adjustedHour).padStart(2, "0")}:${String(nextMinute).padStart(
          2,
          "0"
        )}`
      );
      alert("Please select a time at least 10 minutes from now");
    }
  };

  // UPDATED: Quick pick times based on 10-minute buffer
  const getQuickPickTimes = () => {
    const times = [
      "08:00",
      "08:30",
      "09:00",
      "09:30",
      "12:00",
      "12:30",
      "15:00",
      "15:30",
      "18:00",
      "18:30",
      "21:00",
      "21:30",
    ];

    return times.filter((time) => isValidTime(time));
  };

  if (!open) return null;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(248,250,252,0.85) 100%)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(226,232,240,0.8)",
          boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#111827",
                color: "white",
                fontWeight: 800,
              }}
            >
              <CalendarMonthIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, letterSpacing: 0.2, color: "#111827" }}
              >
                {title || "Calendar"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Plan and visualize your content schedule
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Previous Month">
              <IconButton
                onClick={handlePrev}
                size="small"
                sx={{ bgcolor: "#F3F4F6" }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 2,
                bgcolor: "#111827",
                color: "white",
                fontWeight: 800,
                letterSpacing: 0.5,
                boxShadow: "0 8px 24px rgba(17,24,39,0.25)",
              }}
            >
              {currentMonth.format("MMMM YYYY")}
            </Box>
            <Tooltip title="Next Month">
              <IconButton
                onClick={handleNext}
                size="small"
                sx={{ bgcolor: "#F3F4F6" }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Jump to Today">
              <Button
                onClick={handleToday}
                size="small"
                startIcon={<TodayIcon />}
                sx={{
                  ml: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#2563EB",
                  bgcolor: "rgba(37,99,235,0.08)",
                  "&:hover": { bgcolor: "rgba(37,99,235,0.14)" },
                }}
              >
                Today
              </Button>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Platform Filter */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          {availablePlatforms.map((p) => (
            <Chip
              key={p}
              label={p === "all" ? "All" : p}
              onClick={() => setSelectedPlatform(p)}
              sx={{
                height: 28,
                fontWeight: 700,
                letterSpacing: 0.3,
                color: selectedPlatform === p ? "white" : "#374151",
                bgcolor:
                  selectedPlatform === p ? "#111827" : "rgba(17,24,39,0.06)",
                "&:hover": {
                  bgcolor:
                    selectedPlatform === p ? "#111827" : "rgba(17,24,39,0.12)",
                },
              }}
              icon={
                p === "all" ? undefined : (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: getPlatformColor(p),
                      boxShadow: "0 0 0 2px white",
                    }}
                  />
                )
              }
            />
          ))}
        </Stack>
      </Paper>

      {/* Loading indicator */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            textAlign: "center",
            py: 1,
            bgcolor: "rgba(255,255,255,0.9)",
          }}
        >
          <Typography sx={{ color: "#2563EB", fontWeight: 600 }}>
            Loading posts...
          </Typography>
        </Box>
      )}

      {/* Show error if any */}
      {error && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "#FEE2E2",
            color: "#991B1B",
            borderRadius: 2,
          }}
        >
          <Typography>Error loading posts: {error}</Typography>
        </Box>
      )}

      {/* 2-column layout: calendar (left) + details (right) */}
      <Grid container spacing={2}>
        {/* Left: Calendar */}
        <Grid item xs={12} md={8}>
          {/* Days of week header */}
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <Grid item xs key={d}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    textAlign: "center",
                    fontWeight: 800,
                    color: "#6B7280",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {d}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Grid */}
          <Grid container spacing={1} columns={7}>
            {calendarDays.map((day, idx) => {
              const dayPosts = getFilteredPostsForDate(day);
              const totalPosts = dayPosts.length;
              const styles = getCellStyle(day, totalPosts);
              const isCurrentMonth = day.month() === currentMonth.month();
              const isPast = isPastDate(day);
              const isSelectedDay =
                selectedDate && day.isSame(selectedDate, "day"); // ADD

              const allPlatforms = Array.from(
                new Set(dayPosts.flatMap((p) => p.platforms || []))
              ).slice(0, 5);

              return (
                <Grid item xs={1} key={`${day.format("YYYY-MM-DD")}-${idx}`}>
                  <Paper
                    elevation={0}
                    onClick={() =>
                      (!isPast || totalPosts > 0) && handleSelectDay(day)
                    }
                    sx={{
                      minHeight: 128,
                      borderRadius: 3,
                      border: `1px solid ${styles.border}`,
                      background: styles.background,
                      color: styles.textColor,
                      opacity: styles.opacity,
                      boxShadow: styles.shadow,
                      position: "relative",
                      overflow: "hidden",
                      cursor: !isPast || totalPosts > 0 ? "pointer" : "default",
                      transition:
                        "transform .25s ease, box-shadow .25s ease, opacity .25s ease",
                      "&:hover": {
                        transform:
                          !isPast || totalPosts > 0
                            ? "translateY(-4px)"
                            : "none",
                        boxShadow: styles.hoverShadow,
                      },
                    }}
                  >
                    {/* Glimmer pattern for special days */}
                    {(totalPosts >= 3 || day.isSame(dayjs(), "day")) && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "radial-gradient(120px 120px at 80% 0%, rgba(255,255,255,0.25), transparent 60%)",
                          pointerEvents: "none",
                        }}
                      />
                    )}

                    {/* Increased bottom padding so CTA doesn't crowd the edge */}
                    <Box
                      sx={{ p: 1.25, pb: 6, position: "relative", zIndex: 1 }}
                    >
                      {/* Day number and badge */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: totalPosts > 0 ? 0.75 : 0 }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 900,
                            lineHeight: 1.1, // tighter, avoids vertical jitter
                            textShadow:
                              styles.textColor === "#FFFFFF"
                                ? "0 1px 1px rgba(0,0,0,0.15)"
                                : "none",
                          }}
                        >
                          {day.date()}
                        </Typography>

                        {totalPosts > 0 && (
                          <Badge
                            badgeContent={totalPosts > 9 ? "9+" : totalPosts}
                            color="success"
                            sx={{
                              "& .MuiBadge-badge": {
                                background:
                                  totalPosts >= 3
                                    ? "#065F46"
                                    : totalPosts === 2
                                    ? "#10B981"
                                    : "#34D399",
                                color: "white",
                                fontWeight: 900,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                              },
                            }}
                          />
                        )}
                      </Stack>

                      {/* First post time if any */}
                      {totalPosts > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "inline-block",
                            px: 0.75,
                            py: 0.35,
                            borderRadius: 2,
                            fontWeight: 800,
                            // ...existing styles
                          }}
                        >
                          {/* Convert UTC to local time */}
                          {dayjs(dayPosts[0].scheduleDateTime)
                            .tz(localTimezone)
                            .format("HH:mm")}
                          {totalPosts > 1 && ` +${totalPosts - 1}`}
                        </Typography>
                      )}

                      {/* Platform dots */}
                      {totalPosts > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          sx={{ mt: 1.25, flexWrap: "wrap" }}
                        >
                          {allPlatforms.map((pl, i) => (
                            <Tooltip title={pl} key={`${pl}-${i}`}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: getPlatformColor(pl),
                                  border: "2px solid rgba(255,255,255,0.9)",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      )}

                      {/* CTA for empty future date - padded chip, better offsets */}
                      {/* {totalPosts === 0 && isCurrentMonth && !isPast && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            position: "absolute",
                            bottom: 10,
                            left: "50%", // center horizontally
                            transform: "translateX(-50%)",
                            px: 1, // chip padding
                            py: 0.25,
                            borderRadius: 2,
                            whiteSpace: "nowrap", // avoid wrapping
                            maxWidth: "calc(100% - 16px)",
                            bgcolor:
                              styles.textColor === "#FFFFFF"
                                ? "rgba(255,255,255,0.25)"
                                : "rgba(17,24,39,0.06)",
                            color:
                              styles.textColor === "#FFFFFF"
                                ? "white"
                                : "#111827",
                            border:
                              styles.textColor === "#FFFFFF"
                                ? "1px solid rgba(255,255,255,0.35)"
                                : "1px solid rgba(17,24,39,0.08)",
                            backdropFilter: "blur(6px)",
                          }}
                        >
                          <AddIcon sx={{ fontSize: 18 }} />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                          >
                            Schedule
                          </Typography>
                        </Stack>
                      )} */}

                      {/* CTA chip (centered) */}
                      {isCurrentMonth && !isPast && totalPosts === 0 && (
                        <Chip
                          label={isSelectedDay ? "Selected" : "Schedule"}
                          size="small"
                          icon={
                            !isSelectedDay ? (
                              <AddIcon sx={{ fontSize: 16 }} />
                            ) : undefined
                          }
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: "50%",
                            transform: "translateX(-50%)",
                            borderRadius: "9999px",
                            height: 22,
                            px: 0.75,
                            maxWidth: "90%",
                            width: "auto",
                            overflow: "visible",
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
                            bgcolor:
                              styles.textColor === "#FFFFFF"
                                ? "rgba(255,255,255,0.28)"
                                : "rgba(17,24,39,0.08)",
                            color:
                              styles.textColor === "#FFFFFF"
                                ? "white"
                                : "#111827",
                            border:
                              styles.textColor === "#FFFFFF"
                                ? "1px solid rgba(255,255,255,0.35)"
                                : "1px solid rgba(17,24,39,0.12)",
                            "& .MuiChip-label": {
                              px: 0.75,
                              fontWeight: 700,
                              fontSize: 11.5,
                              lineHeight: "20px",
                              whiteSpace: "nowrap",
                            },
                            "& .MuiChip-iconSmall": {
                              mr: 0.25,
                              fontSize: 14,
                              display: { xs: "none", sm: "inline-flex" }, // hide icon on tight spaces
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Right: Inline details panel */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #E5E7EB",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(249,250,251,0.96) 100%)",
              backdropFilter: "blur(10px)",
              position: { md: "sticky" },
              top: { md: 16 },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#111827", color: "white" }}>
                <EventIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {selectedDate
                    ? selectedDate.format("dddd, MMM D")
                    : "Select a date"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280" }}>
                  {selectedPlatform === "all"
                    ? "All platforms"
                    : selectedPlatform}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {!selectedDate ? (
              <Box sx={{ py: 4, textAlign: "center", color: "#6B7280" }}>
                Click a day to view or schedule posts
              </Box>
            ) : (
              <>
                <List sx={{ py: 0 }}>
                  {getFilteredPostsForDate(selectedDate).length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center", color: "#6B7280" }}>
                      No scheduled posts on this date
                    </Box>
                  ) : (
                    getFilteredPostsForDate(selectedDate)
                      .sort((a, b) => {
                        const dateA = dayjs(a.scheduleDateTime).tz(
                          localTimezone
                        );
                        const dateB = dayjs(b.scheduleDateTime).tz(
                          localTimezone
                        );
                        return dateA.valueOf() - dateB.valueOf();
                      })
                      .map((post) => {
                        // Convert UTC to local time
                        const dt = dayjs(post.scheduleDateTime).tz(
                          localTimezone
                        );

                        return (
                          <ListItem
                            key={post.id}
                            alignItems="flex-start"
                            sx={{
                              mb: 1.25,
                              border: "1px solid #F3F4F6",
                              borderRadius: 2,
                              bgcolor: "white",
                              boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                            }}
                            secondaryAction={
                              <Tooltip title="Preview">
                                <IconButton edge="end">
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                                  color: "white",
                                  fontWeight: 900,
                                }}
                              >
                                {dt.format("HH")}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ flexWrap: "wrap" }}
                                >
                                  <Typography sx={{ fontWeight: 800 }}>
                                    {dt.format("HH:mm")}
                                  </Typography>
                                  <Typography sx={{ color: "#6B7280" }}>
                                    •
                                  </Typography>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    {post.title || "Scheduled Post"}
                                  </Typography>
                                </Stack>
                              }
                              secondary={
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  sx={{ mt: 0.75, flexWrap: "wrap" }}
                                >
                                  {(post.platforms || []).map((pl) => (
                                    <Chip
                                      key={`${post.id}-${pl}`}
                                      label={pl}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontWeight: 700,
                                        color: "white",
                                        bgcolor: getPlatformColor(pl),
                                      }}
                                    />
                                  ))}
                                </Stack>
                              }
                            />
                          </ListItem>
                        );
                      })
                  )}
                </List>

                <Divider sx={{ my: 2 }} />

                {/* UPDATED: Time selector with scrollable dropdown */}
                <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#374151" }}
                  >
                    Select Time
                  </Typography>

                  {/* Time dropdown with all 30-minute slots */}
                  <Box
                    sx={{
                      maxHeight: 240,
                      overflowY: "auto",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      bgcolor: "white",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        bgcolor: "#F3F4F6",
                        borderRadius: 2,
                      },
                      "&::-webkit-scrollbar-thumb": {
                        bgcolor: "#9CA3AF",
                        borderRadius: 2,
                        "&:hover": {
                          bgcolor: "#6B7280",
                        },
                      },
                    }}
                  >
                    {getAvailableTimeSlots().map((slot) => (
                      <Box
                        key={slot.value}
                        onClick={() => handleTimeSelect(slot.value)}
                        sx={{
                          px: 2,
                          py: 1.25,
                          cursor: "pointer",
                          bgcolor:
                            selectedTime === slot.value
                              ? "#111827"
                              : "transparent",
                          color:
                            selectedTime === slot.value ? "white" : "#374151",
                          fontWeight: selectedTime === slot.value ? 800 : 600,
                          fontSize: "0.95rem",
                          borderBottom: "1px solid #F3F4F6",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor:
                              selectedTime === slot.value
                                ? "#111827"
                                : "#F9FAFB",
                          },
                          "&:last-child": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        {slot.label}
                      </Box>
                    ))}
                  </Box>

                  {/* Quick picks with 30-minute intervals */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#6B7280",
                        mb: 1,
                        display: "block",
                      }}
                    >
                      Quick Pick
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: "wrap", gap: 0.5 }}
                    >
                      {getQuickPickTimes().map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          onClick={() => handleTimeSelect(t)}
                          sx={{
                            fontWeight: 800,
                            bgcolor:
                              selectedTime === t
                                ? "#111827"
                                : "rgba(17,24,39,0.06)",
                            color: selectedTime === t ? "white" : "#111827",
                            "&:hover": {
                              bgcolor:
                                selectedTime === t
                                  ? "#0B1220"
                                  : "rgba(17,24,39,0.12)",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>

                {/* Schedule button */}
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      if (selectedDate && selectedTime) {
                        const [hh, mm] = selectedTime.split(":");
                        const localDateTime = selectedDate
                          .hour(parseInt(hh, 10))
                          .minute(parseInt(mm, 10))
                          .second(0)
                          .millisecond(0);

                        // Convert local time to UTC before sending to API
                        const utcDateTime = localDateTime
                          .tz(localTimezone)
                          .utc()
                          .format();
                        onDateTimeSelect?.(utcDateTime);
                      }
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      bgcolor: "#111827",
                      "&:hover": { bgcolor: "#0B1220" },
                    }}
                  >
                    Schedule Here
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setSelectedDate(null)}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                  >
                    Clear
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CalendarView;
